/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

/*
 * ProjectLoadAnalytics is a singleton class that is used to recording timing information
 * for loading a projects assets
 *
 * Order of operations:
 * 0. ProjectShow: ProjectLoadAnalytics.resetInstance() - clears any previous interactions, so we can record for multiple projects
 * 1. ProjectOwnerView: ProjectLoadAnalytics.getInstance().startAssetLoading() - to track the number of assets we're expecting to load
 * 2. Within AudioBufferCache.loadRemoteAssets, bracket loading with recordAssetLoadStart and recordAssetLoadEnd
 */

import { AssetId } from "@phonos/types";

type TracerFinalizer = VoidFunction;

export class ProjectLoadAnalytics {
  private static instance: ProjectLoadAnalytics | null = null;

  private numAssetsToLoad = 0;
  private numLoadedAssets = 0;
  private newrelicInteraction: newrelic.BrowserInteraction;
  private sent = false;

  private assetTracers: Record<AssetId, TracerFinalizer> = {};
  private assetData: Record<AssetId, Record<string, any>> = {};

  private constructor() {
    this.newrelicInteraction = newrelic.interaction();
    this.newrelicInteraction.setName("ProjectLoad");
    this.newrelicInteraction.save(); // save marks it for saving, it doesn't end the interactions
  }

  public static resetInstance(): void {
    ProjectLoadAnalytics.instance = null;
  }

  public static getInstance(): ProjectLoadAnalytics {
    if (!ProjectLoadAnalytics.instance) {
      ProjectLoadAnalytics.instance = new ProjectLoadAnalytics();
    }
    return ProjectLoadAnalytics.instance;
  }

  started(): boolean {
    return this.numAssetsToLoad > 0;
  }

  startAssetLoading(value: number): void {
    this.numAssetsToLoad = value;
  }

  recordAssetLoadStart(assetId: string): void {
    this.assetData[assetId] = {};
    this.assetTracers[assetId] = this.newrelicInteraction.createTracer(
      `Load Asset ${assetId}`
    );
  }

  recordAssetLoadEnd(assetId: string): void {
    this.assetTracers[assetId]?.();
    this.numLoadedAssets++;
    this._saveIfAllDataSet();
  }

  setAssetContentLength(assetId: AssetId, contentLength: number): void {
    this.assetData[assetId]["content-length"] = contentLength;
  }

  setAssetDuration(assetId: string, duration: number): void {
    this.assetData[assetId]["duration"] = duration;
  }

  abortLoad(): void {
    if (!this.started() || this.sent) {
      return;
    }

    // using a tracer for this will give us info about how long it occurred after the load started
    this.newrelicInteraction.createTracer("abort")();
    this.newrelicInteraction.setAttribute("aborted", true);
    Object.values(this.assetTracers).forEach((tracer) => tracer()); // finalize all tracers.
    this._sendEvent();
  }

  private _sendEvent(): void {
    if (!this.newrelicInteraction) {
      return;
    }

    this.sent = true;

    this.newrelicInteraction.setAttribute("assetData", this.assetData);

    this.newrelicInteraction.end();
  }

  private _saveIfAllDataSet(): void {
    if (this.numAssetsToLoad === this.numLoadedAssets) {
      this._sendEvent();
    }
  }
}
